import * as Y from 'yjs';

import type { Node, Edge } from '../../../core/models/scene';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';

type Dict = Record<string, unknown>;

const asRecord = (value: unknown): Dict | null => {
  if (!value || typeof value !== 'object') return null;
  return value as Dict;
};

const asNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const resolveDim = (value: Dict, key: 'width' | 'height'): number => {
  return resolveEffectiveShapeDimension(value, key) ?? 0;
};

export const collectOrgChartSceneFromDoc = (doc: Y.Doc): { nodes: Node[]; edges: Edge[] } => {
  const shapesMap = doc.getMap<unknown>('shapes');
  const connectorsMap = doc.getMap<unknown>('connectors');

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  shapesMap.forEach((raw, id) => {
    const value = asRecord(readYjsValue(raw));
    if (!value) return;

    const attrs = asRecord(value.attrs) ?? {};
    const props = asRecord(value.props) ?? {};
    const zFromTop = typeof value.zIndex === 'number' ? value.zIndex : undefined;
    const zFromProps = typeof props.zIndex === 'number' ? props.zIndex : undefined;
    const zFromAttrs = typeof attrs.zIndex === 'number' ? attrs.zIndex : undefined;
    const zIndex = zFromTop ?? zFromProps ?? zFromAttrs;
    const node: Node = {
      id,
      type: asString(value.type) ?? 'shape',
      x: asNumber(value.x, 0),
      y: asNumber(value.y, 0),
      width: resolveDim(value, 'width'),
      height: resolveDim(value, 'height'),
      w: typeof value.w === 'number' ? value.w : undefined,
      h: typeof value.h === 'number' ? value.h : undefined,
      zIndex,
      name: asString(value.name),
      attrs,
      props,
    };
    nodes.push(node);
  });

  connectorsMap.forEach((raw, id) => {
    const value = asRecord(readYjsValue(raw));
    if (!value) return;
    const attrs = asRecord(value.attrs) ?? {};
    const props = asRecord(value.props) ?? {};

    const from = asString(value.from) ?? asString(value.sourceShapeId);
    const to = asString(value.to) ?? asString(value.targetShapeId);
    if (!from || !to) return;

    const edge: Edge = {
      id,
      type: (asString(value.type) as Edge['type'] | undefined) ?? 'line',
      from,
      to,
      attrs: {
        ...attrs,
        ...props,
      },
    };
    edges.push(edge);
  });

  return { nodes, edges };
};
